import { memo, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectActiveChartId } from '../../../../selectors/chart'
import { AppContext } from '../../../../App'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingSettingItem,
    LineTypes,
    Point,
    SettingValues
} from '../../drawingTools/drawing.interface'
import useStyles from './styles'
import { SettingType } from '../../drawingTools/drawing.enums'
import ColorSelector from '../../../colorSelector/colorSelector'
import StrokeWidthSelector from '../strokeWidthSelector/strokeWidthSelector'
import CoordinateField from '../coordinateField/coordinateField'
import LineTypeSelector from '../lineTypeSelector/lineTypeSelector'
import LineHeadSelector from '../lineHeadSelector/lineHeadSelector'
import { LineHeadType } from '../../drawingTools/lineDrawingTool/lineDrawingTool'
import { Checkbox, MenuItem, Select, TextField } from '@mui/material'
import Chart from '../../chart'
import FontSizeSelector from '../fontSizeSelector/fontSizeSelector'
import NumberSelector from '../numberSelector/numberSelector'


export type ChartDrawingSettingsGroupProps = {
    id: string,
    group: DrawingSettingGroup<any, SettingValues>,
    extraSettingGroup?: boolean,
    onSettingGroupUpdated?: (sg: DrawingSettingGroup<any, SettingValues>) => void,
    chart: Chart
}

export type DrawingSettingFieldProps = {
    id: string,
    setting: DrawingSettingItem<any, SettingValues>,
    value: SettingValues,
    onValueChange: (name: string, value: SettingValues) => void,
    chart: Chart
}

const toExtraSettingStoredValue = (setting: DrawingSettingItem<any, SettingValues>, value: SettingValues): SettingValues => {
    if (setting.type === SettingType.BOOLEAN) {
        return value === true || value === 'true'
    }
    return value
}

const fromExtraSettingStoredValue = (setting: DrawingSettingItem<any, SettingValues>, value: SettingValues): SettingValues => {
    if (setting.type === SettingType.BOOLEAN) {
        return value === true || value === 'true'
    }
    return value
}


export const DrawingSettingField: React.FC<DrawingSettingFieldProps> = ({ setting, value, onValueChange, chart }) => {
    const { classes } = useStyles()
    const { name, type, options, range = [] } = setting
    let component: ReactNode = <></>
    switch (type) {
        case SettingType.COLOR:
            const onColorChange = (color: string, opacity: number) => {
                onValueChange(name.toString(), {
                    color,
                    opacity
                })
            }
            component = <ColorSelector compact showOnlySwatch color={(value as ColorValue).color} opacity={(value as ColorValue).opacity}
                                       onColorChange={onColorChange} />
            break
        case SettingType.STROKE_WIDTH:
            component = <StrokeWidthSelector value={value as number} onChange={(value) => {
                onValueChange(name.toString(), value)
            }} />
            break
        case SettingType.LINE_TYPE:
            component = <LineTypeSelector value={value as LineTypes} onChange={(value) => {
                onValueChange(name.toString(), value)
            }} />
            break
        case SettingType.LINE_HEAD_TYPE:
            component = <LineHeadSelector value={value as LineHeadType} onChange={(value) => {
                onValueChange(name.toString(), value)
            }} />
            break
        case SettingType.COORDINATE:
            component = <CoordinateField chart={chart} setting={setting} value={value as Point} onChange={(value) => {
                onValueChange(name.toString(), value)
            }} />
            break
        case SettingType.BOOLEAN:
            component = <Checkbox size="small" checked={Boolean(value === true || value === 'true')} onChange={(e) => {
                onValueChange(name.toString(), e.target.checked)
            }} />
            break
        case SettingType.SELECT:
            component = options ? <Select size="small" value={value as string} onChange={(e) => {
                onValueChange(name.toString(), e.target.value)
            }}>
                {options.map((o) => <MenuItem key={o.value as string} value={o.value as string}>{o.label}</MenuItem>)}
            </Select> : null
            break
        case SettingType.NUMBER:
            component = <NumberSelector value={value as number} onChange={(value) => {
                onValueChange(name.toString(), value)
            }} range={range} />
            break
        case SettingType.FONT:
            component = <FontSizeSelector value={value as number} onChange={(value) => {
                onValueChange(name.toString(), value)
            }} />
            break
        case SettingType.TEXT:
            component = <TextField size="small" value={value} type="text" onChange={(e) => {
                onValueChange(name.toString(), e.target.value)
            }} />
            break
        case SettingType.TEXT_AREA:
            component = <TextField autoFocus={true} fullWidth={true} multiline={true} minRows={4} size="small"
                                   value={value === 'Add text' ? '' : value} type="text" onChange={(e) => {
                if (e.target.value.trim().length <= 0) {
                    onValueChange(name.toString(), 'Add text')
                } else {
                    onValueChange(name.toString(), e.target.value)
                }
            }} />
    }
    return <div className={classes.settingRow}>
        <div className={classes.label}>{setting.label}</div>
        <div className={classes.value}>
            {component}
        </div>
    </div>
}
const ChartDrawingSettingsGroup: React.FC<ChartDrawingSettingsGroupProps> = ({
                                                                                 id,
                                                                                 group,
                                                                                 chart,
                                                                                 onSettingGroupUpdated,
                                                                                 extraSettingGroup
                                                                             }) => {

    const { classes } = useStyles()
    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)
    const [settingsValues, setSettingsValues] = useState<any>()

    useEffect(() => {
        if (extraSettingGroup) {
            setSettingsValues(group.settings.reduce((acc: any, curr) => {
                acc[curr.name as string] = fromExtraSettingStoredValue(curr, curr.defaultValue)
                return acc
            }, {}))
        } else {
            setSettingsValues(appContext.getChart(activeChartId).getSettingsValues(id))
        }
    }, [id, activeChartId, extraSettingGroup, group])

    const onValueChange = useCallback((name: string, value: SettingValues) => {
        if (extraSettingGroup) {
            const setting = group.settings.find((si) => si.name.toString() === name)
            const storedValue = setting ? toExtraSettingStoredValue(setting, value) : value
            const newSettingsValues = {
                ...settingsValues,
                [name]: storedValue
            }
            setSettingsValues(newSettingsValues)
            onSettingGroupUpdated && onSettingGroupUpdated({
                ...group,
                settings: group.settings.map((si) => ({
                    ...si,
                    defaultValue: newSettingsValues[si.name.toString()]
                }))
            })
        } else {
            appContext.getChart(activeChartId).updateDrawingSettings(id, {
                [name]: value
            })
            setSettingsValues(appContext.getChart(activeChartId).getSettingsValues(id))
        }
    }, [id, activeChartId, onSettingGroupUpdated, extraSettingGroup, settingsValues, group])

    const settingsItems: ReactNode[] = useMemo(() => {
        if (settingsValues) {
            return group.settings.map((s) => {
                return <DrawingSettingField key={String(s.name)} chart={chart} id={id} setting={s} onValueChange={onValueChange}
                                            value={settingsValues[s.name.toString()]} />
            })
        } else {
            return []
        }
    }, [group, settingsValues, id, onValueChange])

    return (
        <div className={classes.container}>
            {settingsItems}
        </div>
    )
}

export default memo(ChartDrawingSettingsGroup)
